import { useState } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, ChevronsLeft, ChevronsRight } from 'lucide-react';
import {
  FaLinkedin,
  FaWhatsapp,
  FaTiktok,
  FaYoutube,
  FaXTwitter,
  FaInstagram,
  FaFacebook,
} from 'react-icons/fa6';
import useHandleLogout from 'src/hooks/useHandleLogout';
import MenuItems from './MenuItems';
import { useNavGroups } from './navItems';
import { cn } from '../ui-x';

// Served from `public/`, not imported: CRA runs `src` SVG imports through
// SVGR, which cannot parse this file's CorelDRAW `<style><![CDATA[...]]>`
// block. A URL keeps the artwork byte-identical to the other repos'.
const LogoLockup = `${process.env.PUBLIC_URL}/assets/droomlogo.svg`;

export const socials = [
  {
    href: 'https://twitter.com/Droom__work?t=KQdoCJ4aRxXFaoRTCgu0oA&s=08/',
    label: 'X',
    icon: <FaXTwitter className='h-4 w-4' />,
  },
  {
    href: 'https://www.instagram.com/droom__work/',
    label: 'Instagram',
    icon: <FaInstagram className='h-4 w-4' />,
  },
  {
    href: 'https://www.facebook.com/droomwork',
    label: 'Facebook',
    icon: <FaFacebook className='h-4 w-4' />,
  },
  {
    href: 'https://www.linkedin.com/company/droomwork',
    label: 'LinkedIn',
    icon: <FaLinkedin className='h-4 w-4' />,
  },
  {
    href: 'https://chat.whatsapp.com/H1noCQDkzIHDnNzry0u18z?mode=ems_copy_t',
    label: 'WhatsApp',
    icon: <FaWhatsapp className='h-4 w-4' />,
  },
  {
    href: 'https://chat.whatsapp.com/H1noCQDkzIHDnNzry0u18z?mode=ems_copy_t',
    label: 'TikTok',
    icon: <FaTiktok className='h-4 w-4' />,
  },
  {
    href: 'https://chat.whatsapp.com/H1noCQDkzIHDnNzry0u18z?mode=ems_copy_t',
    label: 'YouTube',
    icon: <FaYoutube className='h-4 w-4' />,
  },
];

export const SocialLinks = () => (
  <div className='flex flex-wrap items-center gap-x-3 gap-y-2'>
    {socials.map(({ href, label, icon }) => (
      <a
        key={label}
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        aria-label={label}
        title={label}
        className='text-muted-foreground transition-colors hover:text-brand-orange'
      >
        {icon}
      </a>
    ))}
  </div>
);

const SideNav = () => {
  const { handleLogout } = useHandleLogout();
  const { primary, secondary } = useNavGroups();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className={cn(
        'sticky top-0 hidden h-screen shrink-0 flex-col border-r bg-card transition-[width] duration-200 lg:flex',
        collapsed ? 'w-16' : 'w-60'
      )}
    >
      <div
        className={cn(
          'flex h-14 shrink-0 items-center border-b px-3',
          collapsed ? 'justify-center' : 'justify-between'
        )}
      >
        {/* The original Droomwork lockup — swirl and word as one piece of
            artwork rather than the mark set beside live text. width/height
            carry the artwork's own 63.75:17.5 ratio so the row reserves its
            space before the file lands; `h-8 w-auto` is what sizes it. */}
        {!collapsed && (
          <Link
            to='/dashboard'
            aria-label='Droomwork home'
            className='flex items-center px-1'
          >
            <img
              src={LogoLockup}
              alt=''
              aria-hidden='true'
              width={255}
              height={70}
              className='h-8 w-auto'
            />
          </Link>
        )}
        <button
          type='button'
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className='rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-surface hover:text-brand-navy'
        >
          {collapsed ? (
            <ChevronsRight className='h-4 w-4' />
          ) : (
            <ChevronsLeft className='h-4 w-4' />
          )}
        </button>
      </div>

      <nav className='flex-1 space-y-6 overflow-y-auto p-3'>
        <MenuItems label='My Employment' items={primary} collapsed={collapsed} />
        <MenuItems
          label='Explore on Droomwork'
          items={secondary}
          collapsed={collapsed}
          muted
        />
      </nav>

      <div className='shrink-0 space-y-4 border-t p-3'>
        <button
          type='button'
          onClick={() => handleLogout()}
          title={collapsed ? 'Sign out' : undefined}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-surface',
            collapsed && 'justify-center'
          )}
        >
          <LogOut className='h-4 w-4 shrink-0' />
          {!collapsed && 'Sign out'}
        </button>

        {!collapsed && (
          <div className='px-3'>
            <div className='pb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>
              Follow us
            </div>
            <SocialLinks />
          </div>
        )}
      </div>
    </aside>
  );
};

export default SideNav;
